import {
  LoaderSpinnerModal,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';
import React, { useEffect, useState } from 'react';
import { fetchRecall } from '../../fetch-data/fetch-recall-data';
import { getStringifiedServiceConfigsServiceConfig } from '../../services/get-stringified-service-configs-service-config';
import { useFeatureAppConfig } from '../../services/use-feature-app-config';
import { useFeatureServices } from '../../services/use-feature-services';
import { RecallsAppModel } from '../../typings/general';
import ResultsSection from '../results-section';
import CopySection from './copy-section';
import InputLabel from './input-label';
import RecallsInput from './recalls-input';
import { RecallsAppContainer, StyledHelperText } from './styled';
import { useTrackingManager } from '../../services/use-tracking-manager';

export interface ErrorState {
  vin?: string;
  state: boolean;
  message?: string;
}

const RecallsApp = (): JSX.Element => {
  const [recall, setRecall] = useState<RecallsAppModel | undefined>();
  const [errorRequest, setErrorRequest] = useState<ErrorState>({
    state: false,
  });
  const [pendingFetch, setPendingFetch] = useState(false);
  const trackingManager = useTrackingManager();

  const initialVin = '00000000000000000';

  const helperDescription =
    '*Please note that the VIN (Vehicle Identification Number) does not use the letters “O, ” “I, ” or “Q.”';

  // Request data
  const {
    'locale-service': localeService,
    'service-config-provider': serviceProvider,
    's2:server-request': serverRequest,
  } = useFeatureServices();

  const serviceConfigsServiceConfig = getStringifiedServiceConfigsServiceConfig(
    serviceProvider,
    serverRequest,
  );
  const { mockIds, mocksBaseUrl, faServicesUrl } = useFeatureAppConfig();

  /**
   *  fetch data according to typed vin
   * @param vin
   */
  const getRecallData = (vin: string): void => {
    setPendingFetch(true);
    fetchRecall({
      vin,
      faServicesUrl,
      mockIds,
      mocksBaseUrl,
      localeService,
      serviceConfigsServiceConfig,
    })
      .then((recallResult: RecallsAppModel) => {
        setPendingFetch(false);
        let errorState: ErrorState;
        if (recallResult) {
          if (recallResult.status) {
            setRecall(recallResult);
            vin !== initialVin &&
              trackingManager.trackRecallsGlobalEvent(
                'VWBasic_VINFormSubmissionSuccessMessage_Load',
                {
                  vin,
                  FormName: 'Recall request',
                  FormType: 'otherform',
                  FormVersion: '1',
                },
                undefined,
              );
          }
          errorState = {
            state: !recallResult.status,
            message: recallResult.errorMessage,
            vin: recallResult.vin,
          };
        } else {
          errorState = { state: true, vin };
        }
        setErrorRequest(errorState);
      })
      .catch((error: string) => {
        setPendingFetch(false);
        console.log(error);
        const errorState = { state: true, vin };
        setErrorRequest(errorState);
      });
  };

  /**
   * Submit Handler Data
   * @param vin
   */
  const onSubmitHandler = (vin: string) => {
    getRecallData(vin);

    trackingManager.trackRecallsGlobalEvent(
      'VWBasic_VINFormCTA_Click',
      {
        FormName: 'Recall request',
        FormType: 'otherform',
        FormVersion: '1',
        linkUrl: 'Search Vin',
        linkName: 'Submit',
        vin: vin,
      },
      undefined,
    );
  };

  // Load initial data
  useEffect(() => {
    getRecallData(initialVin);
  }, []);
  const showRecallData: boolean =
    (!errorRequest.state && recall && recall.vin !== initialVin) || false;

  return (
    <div id="recallsFA">
      <RecallsAppContainer>
        <InputLabel date={recall?.refreshDate} />
        <RecallsInput
          onSubmitHandler={onSubmitHandler}
          errorRequest={errorRequest}
        />
        <StyledHelperText withPadding={!showRecallData}>
          <TextCustom
            appearance={TextAppearance.headline200}
            style={{
              color: TextColor.secondary,
              fontSize: TextSize.TextSize12,
              lineHeight: TextLineHeight.TextLineHeight18,
              fontWeight: TextWeight.regular,
            }}
          >
            {helperDescription}
          </TextCustom>
        </StyledHelperText>
        {showRecallData && <ResultsSection recallData={recall} />}
        <CopySection />
      </RecallsAppContainer>
      {pendingFetch && <LoaderSpinnerModal />}
    </div>
  );
};

export default RecallsApp;
